/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';

import {capturePreviewSecretFromUrl} from '@amblique/contentful/app/utils/preview-secret';
import {isPreviewPathname} from '@amblique/king-living/app/utils/preview-path';

/**
 * URL-scoped, cookie-less Contentful preview state for the current page.
 *
 * Preview is scoped to the URL, not the whole session: the `/{site}/preview/...`
 * path renders drafts while the normal URL stays published. Two signals combine:
 *  - `isPreviewPath` — the current path is a `/preview` path (deterministic on
 *    SSR + client, so it never causes a hydration mismatch).
 *  - `isAuthorized` — a preview secret is held in memory. It is captured from the
 *    `?secret=` URL once (then the secret is stripped from the URL bar) and lives
 *    in memory only — so a full refresh requires re-opening the preview link.
 *    Read after mount only (false on SSR + first client render) to avoid
 *    hydration warnings, then flips to the real value.
 *
 * `isActive` (= path && authorized) is what flips `ContentfulService` into
 * preview mode. On a preview path WITHOUT a captured secret, `isActive` is false
 * (the page renders published) and the banner shows a "not authorized" notice.
 *
 * @returns {{isPreviewPath: boolean, isAuthorized: boolean, isActive: boolean}}
 */
export function usePreviewState() {
  const {pathname} = useLocation();
  const isPreviewPath = isPreviewPathname(pathname);

  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    // Capture (+ strip) the `?secret=` on first load; on later in-tab navigation
    // this returns the value already in memory, so preview persists across the tab.
    setIsAuthorized(Boolean(capturePreviewSecretFromUrl()));
  }, [pathname]);

  return {
    isPreviewPath,
    isAuthorized,
    isActive: isPreviewPath && isAuthorized,
  };
}

/**
 * Boolean convenience: whether draft content should be fetched for this render.
 * Consumed by the `useService` override to fold `preview` into the service hash.
 *
 * @returns {boolean}
 */
export function usePreview() {
  return usePreviewState().isActive;
}

export default usePreview;
